/// TaskFormRouter: Maps formKey from user task custom headers to a React form
/// component. Falls back to JsonSchemaForm for unregistered formKeys.
/// Provides the TaskFormProps interface that all form components must implement.

import React from "react";
import { useEngineStore, type UserTask } from "@/stores/engineStore";
import { useUserTasks } from "@/hooks/useUserTasks";
import { ApprovalForm } from "./forms/ApprovalForm";
import { JsonSchemaForm } from "./forms/JsonSchemaForm";

// ---------------------------------------------------------------------------
// Form props interface (exported for form components)
// ---------------------------------------------------------------------------

export interface TaskFormProps {
  task: UserTask;
  onComplete: (variables: Record<string, unknown>) => Promise<void>;
  onFail: (reason: string) => Promise<void>;
}

// ---------------------------------------------------------------------------
// Form registry: maps formKey -> React form component
// ---------------------------------------------------------------------------

const formRegistry: Record<string, React.ComponentType<TaskFormProps>> = {
  "approval-form": ApprovalForm,
  approval: ApprovalForm,
  // Add more built-in form components here as needed.
  // External form registrations could be added via a registerForm() API.
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TaskFormRouter() {
  const userTasks = useEngineStore((s) => s.userTasks);
  const activeTaskJobKey = useEngineStore((s) => s.activeTaskJobKey);
  const { completeTask, failTask } = useUserTasks();

  const activeTask = userTasks.find((t) => t.jobKey === activeTaskJobKey);

  if (!activeTask) {
    return (
      <div className="flex items-center justify-center h-full text-xs text-zinc-500">
        Select a task to view its form
      </div>
    );
  }

  // Look up form component from registry; fall back to JsonSchemaForm
  const FormComponent = formRegistry[activeTask.formKey] ?? JsonSchemaForm;

  return (
    <div className="p-4 overflow-y-auto h-full">
      {/* Task header */}
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-zinc-200">{activeTask.taskName}</h3>
        <p className="text-xs text-zinc-500">
          Form: {activeTask.formKey}
          {activeTask.assignee && ` | Assignee: ${activeTask.assignee}`}
        </p>
      </div>

      {/* Rendered form */}
      <FormComponent
        task={activeTask}
        onComplete={(variables) => completeTask(activeTask.jobKey, variables)}
        onFail={(reason) => failTask(activeTask.jobKey, reason)}
      />
    </div>
  );
}
